import React, { useState, useRef } from 'react';
import { X, Upload, Download, FileText, WarningCircle, CheckCircle, Info } from '@phosphor-icons/react';
import fileImportService from '../services/fileImportService';

const FileImportModal = ({ isOpen, onClose, onImport }) => {
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [parsedData, setParsedData] = useState(null);
  const [errors, setErrors] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const fileInputRef = useRef(null);

  const resetState = () => {
    setSelectedFile(null);
    setParsedData(null);
    setErrors([]);
    setShowPreview(false);
    setShowHelp(false);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = async (file) => {
    setSelectedFile(file);
    setErrors([]);
    setParsedData(null);
    setShowPreview(false);

    // Validate file
    const validation = fileImportService.validateFile(file);
    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }

    // Parse file
    setIsProcessing(true);
    try {
      const rawData = await fileImportService.parseFile(file);
      const result = fileImportService.validateAndMapData(rawData);
      
      setParsedData(result);
      setErrors(result.errors);
      
      if (result.errors.length === 0) {
        setShowPreview(true);
      }
    } catch (error) {
      setErrors([error.message]);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleImport = () => {
    if (parsedData && parsedData.errors.length === 0) {
      onImport(parsedData);
      handleClose();
    }
  };

  const downloadTemplate = () => {
    fileImportService.downloadTemplate();
  };

  const columnDescriptions = fileImportService.getColumnDescriptions();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Import Locations & Doctors</h2>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Help Section */}
          {showHelp && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start justify-between mb-3">
                <h3 className="font-semibold text-blue-900">File Format Requirements</h3>
                <button
                  onClick={() => setShowHelp(false)}
                  className="text-blue-600 hover:text-blue-800"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="space-y-3">
                <p className="text-sm text-blue-800">
                  Your file must contain the following columns:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                  {Object.entries(columnDescriptions).map(([column, description]) => (
                    <div key={column} className="flex flex-col">
                      <span className="font-medium text-blue-900">{column}</span>
                      <span className="text-blue-700">{description}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-3 p-3 bg-blue-100 rounded">
                  <p className="text-sm text-blue-800">
                    <strong>Note:</strong> You can have multiple doctors at the same location, or one doctor at multiple locations. 
                    Just repeat the location information for each doctor-location combination. 
                    Doctors with the same name will be automatically merged with all their locations.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* File Upload Area */}
          {!selectedFile && (
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                dragActive 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-gray-300 hover:border-gray-400'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Upload CSV or Excel File
              </h3>
              <p className="text-gray-500 mb-4">
                Drag and drop your file here, or click to browse
              </p>
              <div className="space-y-3">
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Choose File
                </button>
                <div className="text-sm text-gray-500">
                  <p>Supported formats: CSV, XLSX, XLS</p>
                  <p>Maximum file size: 10MB</p>
                </div>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileInput}
                className="hidden"
              />
            </div>
          )}

          {/* File Processing */}
          {isProcessing && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Processing file...</p>
            </div>
          )}

          {/* Errors */}
          {errors.length > 0 && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center mb-2">
                <WarningCircle className="w-5 h-5 text-red-500 mr-2" />
                <h3 className="font-semibold text-red-900">Import Errors</h3>
              </div>
              <ul className="text-sm text-red-800 space-y-1">
                {errors.map((error, index) => (
                  <li key={index}>• {error}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Preview */}
          {showPreview && parsedData && (
            <div className="mb-6">
              <div className="flex items-center mb-4">
                <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                <h3 className="font-semibold text-green-900">File Ready for Import</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <h4 className="font-medium text-green-900 mb-2">Locations to Create</h4>
                  <p className="text-2xl font-bold text-green-600">{parsedData.locations.length}</p>
                  <p className="text-sm text-green-700">
                    {parsedData.locations.map(loc => loc.name).join(', ')}
                  </p>
                </div>
                
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h4 className="font-medium text-blue-900 mb-2">Doctors to Create</h4>
                  <p className="text-2xl font-bold text-blue-600">{parsedData.doctors.length}</p>
                  <p className="text-sm text-blue-700">
                    {parsedData.doctors.map(doc => `${doc.name} (${doc.locationNames?.length || 0} locations)`).join(', ')}
                  </p>
                </div>
              </div>
              
              {/* Deduplication Info */}
              {parsedData.doctors.length > 0 && (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg mb-4">
                  <div className="flex items-center mb-2">
                    <Info className="w-5 h-5 text-yellow-600 mr-2" />
                    <h4 className="font-medium text-yellow-900">Import Preview</h4>
                  </div>
                  <p className="text-sm text-yellow-800">
                    Doctors with multiple locations have been automatically merged. Each doctor will be created once with all their associated locations.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={downloadTemplate}
              className="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Download className="w-4 h-4 mr-2" />
              Download Template
            </button>
            
            <button
              onClick={() => setShowHelp(!showHelp)}
              className="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Info className="w-4 h-4 mr-2" />
              {showHelp ? 'Hide Help' : 'Show Help'}
            </button>
            
            {selectedFile && (
              <button
                onClick={() => {
                  setSelectedFile(null);
                  setParsedData(null);
                  setErrors([]);
                  setShowPreview(false);
                }}
                className="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <FileText className="w-4 h-4 mr-2" />
                Choose Different File
              </button>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          
          {showPreview && parsedData && parsedData.errors.length === 0 && (
            <button
              onClick={handleImport}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Import {parsedData.locations.length + parsedData.doctors.length} Items
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default FileImportModal;
